import React, {ChangeEvent, useCallback, useState} from 'react';

import {CommonInputInterface} from '../common-input-interface';
import {useValue, useFocus} from '../input-hooks';
import {Field} from '../field';
import {CrossIcon, EyeCloseIcon, EyeOpenIcon} from '../../svg-icon';
import {Button} from '../button';
import {cls, useUniqueId} from '../../../utils';

import css from '../input/input.module.css';
import fieldCss from '../field/field.module.css';

interface InputInterface extends CommonInputInterface<string> {
	setRef?: React.RefObject<HTMLInputElement | null>;
}

export type InputProps = InputInterface & Omit<React.InputHTMLAttributes<HTMLInputElement>, keyof InputInterface>;

export const Password: React.FC<InputProps> = (props) => {
	const {error, disabled, className, appearance, size, shape, helperText, onChange: _, setRef, ...inputProps} = props;
	const {hasFocus, onBlur, onFocus} = useFocus(props);
	const {value, onChange} = useValue(props);
	const [open, setOpen] = useState(false);

	const handleToggleOpen = useCallback(() => {
		setOpen(!open);
	}, [open]);

	const handleClearValue = useCallback(() => {
		onChange({} as ChangeEvent<HTMLInputElement>);
	}, []);

	const generatedId = useUniqueId('Input-Password');
	const id = props.id || generatedId;

	return (
		<Field
			commonProps={props}
			topLabel={!!value || hasFocus}
			className={className}
			appearance={appearance ?? 'outline'}
			size={size}
			shape={shape}
			buttons={
				<>
					{value?.toString() && !disabled && (
						<Button
							appearance="subtle"
							icon={<CrossIcon width={14} height={14} />}
							onClick={handleClearValue}
							className={fieldCss.actionButton}
						/>
					)}
					<Button
						appearance="subtle"
						icon={open ? <EyeCloseIcon width={14} height={14} /> : <EyeOpenIcon width={14} height={14} />}
						onClick={handleToggleOpen}
						className={fieldCss.actionButton}
					/>
				</>
			}
		>
			<input
				id={id}
				disabled={disabled}
				className={cls(css.input, hasFocus && css.hasFocus)}
				autoComplete="new-password"
				aria-autocomplete="list"
				{...(inputProps as any)}
				type={open ? 'text' : 'password'}
				onChange={onChange}
				onBlur={onBlur}
				onFocus={onFocus}
				ref={setRef}
				value={value}
			/>
		</Field>
	);
};
